import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { apiService } from "@/services/api.service";

export interface LeoMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
  toolsUsed?: string[];
}

interface LeoChatState {
  messages: LeoMessage[];
  isOpen: boolean;
  isLoading: boolean;
  currentPage: string;
}

interface LeoChatActions {
  togglePanel: () => void;
  openPanel: () => void;
  closePanel: () => void;
  sendMessage: (text: string) => Promise<void>;
  clearHistory: () => void;
  setCurrentPage: (page: string) => void;
}

type LeoChatStore = LeoChatState & LeoChatActions;

const WELCOME_MESSAGE: LeoMessage = {
  id: "welcome",
  role: "assistant",
  content:
    "Ola! Sou o **Leo**, assistente do Portal CaaS da Liqi.\n\nPosso consultar seus dados (saldos, ordens, movimentacoes) e tirar duvidas sobre o portal.\n\nComo posso te ajudar?",
  timestamp: new Date(),
};

export const useLeoChatStore = create<LeoChatStore>()(
  devtools(
    (set, get) => ({
      messages: [WELCOME_MESSAGE],
      isOpen: false,
      isLoading: false,
      currentPage: "resumo",

      togglePanel: () => set((s) => ({ isOpen: !s.isOpen })),
      openPanel: () => set({ isOpen: true }),
      closePanel: () => set({ isOpen: false }),
      setCurrentPage: (page) => set({ currentPage: page }),
      clearHistory: () => set({ messages: [WELCOME_MESSAGE] }),

      sendMessage: async (text: string) => {
        const { messages, currentPage } = get();

        const userMessage: LeoMessage = {
          id: `user-${Date.now()}`,
          role: "user",
          content: text,
          timestamp: new Date(),
        };

        set({ messages: [...messages, userMessage], isLoading: true });

        const history = messages
          .filter((m) => m.id !== "welcome")
          .slice(-20)
          .map((m) => ({ role: m.role, content: m.content }));

        try {
          const response = await apiService.post<{
            text: string;
            toolsUsed: string[];
          }>("/chat/message", { message: text, history, context: { currentPage } });

          if (response.ok && response.data) {
            const botMessage: LeoMessage = {
              id: `bot-${Date.now()}`,
              role: "assistant",
              content: response.data.text,
              timestamp: new Date(),
              toolsUsed: response.data.toolsUsed,
            };
            set((s) => ({
              messages: [...s.messages, botMessage],
              isLoading: false,
            }));
          } else {
            const errorMessage: LeoMessage = {
              id: `bot-${Date.now()}`,
              role: "assistant",
              content: "Desculpe, tive um problema ao processar sua mensagem. Tente novamente.",
              timestamp: new Date(),
            };
            set((s) => ({
              messages: [...s.messages, errorMessage],
              isLoading: false,
            }));
          }
        } catch {
          const errorMessage: LeoMessage = {
            id: `bot-${Date.now()}`,
            role: "assistant",
            content: "Desculpe, não consegui me conectar ao servidor. Verifique sua conexão.",
            timestamp: new Date(),
          };
          set((s) => ({
            messages: [...s.messages, errorMessage],
            isLoading: false,
          }));
        }
      },
    }),
    { name: "leo-chat" },
  ),
);
